import type { Manifest } from '@clawharbor/manifest';
import { normalizeResult } from '@clawharbor/render-core';
import type { CapabilityResult } from '@clawharbor/render-core';

/**
 * The browser's only channel to the origin.
 *
 * The page never talks to the tenant's site directly — it asks ClawHarbor's data
 * plane, which runs the manifest's declarative binding server-side through
 * @clawharbor/binding-runtime. That is one code path to the customer's origin,
 * shared with the MCP surface: the tenant's rate limit stays enforceable, the
 * cache is shared, and the browser never needs CORS from a third party.
 */

export interface CallOutcome {
  result: CapabilityResult;
  /** Where the user finishes the job. Present when the binding produced one. */
  deepLink?: string;
  /** Server-authored narration, preferred over render-core's generated summary. */
  summary?: string;
}

export class DataPlaneError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'DataPlaneError';
    this.status = status;
  }
}

/**
 * Tenant resolution, most explicit first.
 *
 * Subdomain comes last because it is right in production
 * (`cinestar.clawharbor.io`) and wrong everywhere else — localhost, an IP, a
 * preview host.
 *
 * The `/t/{tenant}` step is not decoration: it is the server's canonical tenant
 * URL (`registerStaticRoutes` serves the shared bundle there), and the shared
 * bundle ships with an EMPTY `clawharbor-tenant` meta. Without this step every
 * `/t/{tenant}` load on a host with fewer than three DNS labels — localhost, an
 * IP, a preview host — fell through to the last-resort constant and asked for a
 * manifest belonging to some other tenant entirely.
 */
export function resolveTenant(loc: Location = window.location): string {
  const fromQuery = new URLSearchParams(loc.search).get('tenant');
  if (fromQuery) return fromQuery;

  const meta = document.querySelector('meta[name="clawharbor-tenant"]');
  const fromMeta = meta?.getAttribute('content');
  if (fromMeta) return fromMeta;

  const fromPath = /^\/t\/([^/?#]+)/.exec(loc.pathname)?.[1];
  if (fromPath) {
    try {
      return decodeURIComponent(fromPath);
    } catch {
      return fromPath;
    }
  }

  const labels = loc.hostname.split('.');
  const looksLikeIp = /^\d+$/.test(labels[labels.length - 1] ?? '');
  if (labels.length >= 3 && !looksLikeIp) {
    const first = labels[0];
    if (first && first !== 'www' && first !== 'app') return first;
  }
  return 'cinestar-hr';
}

/** Overridable so the static bundle can be hosted apart from the API. */
export function apiBase(): string {
  const meta = document.querySelector('meta[name="clawharbor-api"]');
  return meta?.getAttribute('content')?.replace(/\/$/, '') ?? '';
}

async function readError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string; message?: string };
    return body.error ?? body.message ?? `${res.status} ${res.statusText}`;
  } catch {
    return `${res.status} ${res.statusText}`;
  }
}

export async function fetchManifest(tenant: string, signal?: AbortSignal): Promise<Manifest> {
  const init: RequestInit = { headers: { accept: 'application/json' } };
  if (signal) init.signal = signal;
  const res = await fetch(`${apiBase()}/api/${encodeURIComponent(tenant)}/manifest`, init);
  if (!res.ok) throw new DataPlaneError(await readError(res), res.status);
  const json: unknown = await res.json();
  assertManifestish(json);
  return json;
}

/**
 * Structural check only.
 *
 * Full zod validation happens server-side; re-running it here would drag the
 * entire schema module into a browser bundle to re-check a document we ourselves
 * just served. This exists purely to turn "blank page" into a readable error.
 */
function assertManifestish(value: unknown): asserts value is Manifest {
  const m = value as Partial<Manifest> | null;
  if (!m || typeof m !== 'object') throw new DataPlaneError('manifest response was not an object', 0);
  if (!Array.isArray(m.capabilities)) throw new DataPlaneError('manifest has no capabilities[]', 0);
  if (!Array.isArray(m.views)) throw new DataPlaneError('manifest has no views[]', 0);
  if (!m.brand || !m.identity || !m.policy || !m.source) {
    throw new DataPlaneError('manifest is missing brand/identity/policy/source', 0);
  }
}

export async function callCapability(
  tenant: string,
  capabilityId: string,
  input: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<CallOutcome> {
  const init: RequestInit = {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    body: JSON.stringify(input ?? {}),
  };
  if (signal) init.signal = signal;

  const url = `${apiBase()}/api/${encodeURIComponent(tenant)}/call/${encodeURIComponent(capabilityId)}`;
  const res = await fetch(url, init);
  if (!res.ok) throw new DataPlaneError(await readError(res), res.status);

  const raw: unknown = await res.json();

  /**
   * Never trust the envelope's shape. `normalizeResult` reduces anything
   * result-shaped and never throws — the same discipline the binding layer
   * applies to CineStar's JSON API, where a wrong `datum` format returns a 200
   * with a valid-but-wrong body. A status check catches that; nothing else does.
   */
  const outcome: CallOutcome = { result: normalizeResult(raw) };

  const body = (raw ?? {}) as Record<string, unknown>;
  if (typeof body['deepLink'] === 'string') outcome.deepLink = body['deepLink'];
  if (typeof body['summary'] === 'string') outcome.summary = body['summary'];
  return outcome;
}
